
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  FormControlLabel,
  IconButton, // Added IconButton import
  Button, // Added Button import
} from '@mui/material';
import Modal from '@mui/material/Modal'; // Import Modal
import CustomerUsers from './CustomerUsers'; // Import CustomerUsers
import SearchIcon from '@mui/icons-material/Search';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'; // Import new icon
import DeleteIcon from '@mui/icons-material/Delete'; // Import DeleteIcon
import api from '../../services/api'; // Import the API service

const CloudTelephony = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [customerData, setCustomerData] = useState([]); // Initialize as empty array
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openUsersModal, setOpenUsersModal] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [openCtSettingsModal, setOpenCtSettingsModal] = useState(false); // New state for CT settings modal
  const [tempCtSettings, setTempCtSettings] = useState({ max_users: 100, expiry_date: '2025-12-31' }); // Temp state for new settings
  const [openAddNumberModal, setOpenAddNumberModal] = useState(false); // State for new virtual number modal
  const [newVirtualNumber, setNewVirtualNumber] = useState(''); // State for new virtual number input

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        setLoading(true);
        setError(null);
        // Replace with actual API call
        const response = await api.get('/customers/'); // Use api.get
        const result = response.data; // Axios puts data directly in .data
        
        if (!result.success) {
          throw new Error(result.message || "API call not successful");
        }

        // Map backend data to frontend structure
        const mappedData = result.data.map(customer => ({
          id: customer.id,
          name: customer.company_name,
          adminName: customer.ownerName,
          registeredMobileNumber: customer.phone,
          emailId: customer.email,
          totalUsers: customer.totalUsers || 0,
          ctActive: customer.isCloudTelephonyEnabled || false,
          ctMaxUsers: customer.cloudTelephonyMaxUsers || 0,
          ctActiveUsers: customer.ctActiveUsers || 0, // Add ctActiveUsers
          virtualNumbers: customer.virtualNumbers?.map(vn => vn.number) || [],
          ctExpiryDate: customer.cloudTelephonyExpiryDate || 'N/A',
          vendor: customer.cloudTelephonyVendor || 'N/A',
        }));
        setCustomerData(mappedData);
      } catch (error) {
        console.error("Error fetching customer data:", error.message || error);
        setError(`Failed to load customer data: ${error.message || "Unknown error"}`);
      } finally {
        setLoading(false);
      }
    };
    fetchCustomers();
  }, []);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleToggleCtActive = async (id, currentStatus) => {
    console.log(`Toggle CT Active for customer ID: ${id}, current status: ${currentStatus}`);
    setSelectedCustomerId(id); // Set the customer ID for the modal

    if (!currentStatus) { // If currently inactive, open modal to get settings
      setOpenCtSettingsModal(true);
      return;
    }

    // Optimistically update UI
    setCustomerData((prevData) =>
      prevData.map((customer) =>
        customer.id === id
          ? { ...customer, ctActive: !currentStatus }
          : customer
      )
    );

    try {
      const response = await api.delete(`/account/${id}/cloud-telephony/`, {}); // Only delete when toggling off

      console.log("API response:", response); // Log the response for debugging

      if (response.data && !response.data.success) { // Check for success in Axios response.data
        throw new Error(response.data.message || `API call not successful!`);
      }
    } catch (error) {
      console.error("Error toggling CT Active:", error);
      setError(`Failed to update CT Active status: ${error.message}`);
      // Revert UI on error
      setCustomerData((prevData) =>
        prevData.map((customer) =>
          customer.id === id
            ? { ...customer, ctActive: currentStatus }
            : customer
        )
      );
    }
  };

  const handleSaveCtSettings = async () => {
    if (!selectedCustomerId) return;

    setOpenCtSettingsModal(false); // Close the modal

    // Optimistically update UI
    setCustomerData((prevData) =>
      prevData.map((customer) =>
        customer.id === selectedCustomerId
          ? { ...customer, ctActive: true, ctMaxUsers: tempCtSettings.max_users, ctExpiryDate: tempCtSettings.expiry_date }
          : customer
      )
    );

    try {
      const response = await api.post(`/account/${selectedCustomerId}/cloud-telephony/`, {
        provider: 'KOM', // Assuming 'KOM' as default provider
        max_users: tempCtSettings.max_users,
        expiry_date: tempCtSettings.expiry_date,
      });

      console.log("API response for enabling CT:", response); // Log the response for debugging

      if (response.data && !response.data.success) {
        throw new Error(response.data.message || `API call not successful!`);
      }
    } catch (error) {
      console.error("Error enabling CT Active:", error);
      setError(`Failed to enable CT Active status: ${error.message}`);
      // Revert UI on error
      setCustomerData((prevData) =>
        prevData.map((customer) =>
          customer.id === selectedCustomerId
            ? { ...customer, ctActive: false } // Revert ctActive status
            : customer
        )
      );
    }
  };

  const handleAddNumber = (customerId) => {
    console.log(`Add new virtual number for customer ID: ${customerId}`);
    setSelectedCustomerId(customerId); // Set the customer ID
    setOpenAddNumberModal(true); // Open the modal
  };

  const handleAddNumberConfirm = async () => {
    if (!newVirtualNumber || !selectedCustomerId) return;

    setOpenAddNumberModal(false); // Close the modal
    try {
      const response = await api.post(`/account/${selectedCustomerId}/virtual-numbers/`, {
        number: newVirtualNumber 
      });

      if (response.data && !response.data.success) { // Check for success in Axios response.data
        throw new Error(response.data.message || "Failed to add virtual number");
      }

      // Axios already parses JSON, so response.data is directly the object
      setCustomerData((prevData) =>
        prevData.map((customer) =>
          customer.id === selectedCustomerId
            ? { ...customer, virtualNumbers: [...customer.virtualNumbers, response.data.data.number] }
            : customer
        )
      );
      setNewVirtualNumber(''); // Clear input after successful addition
    } catch (error) {
      console.error("Error adding virtual number:", error);
      setError(`Failed to add virtual number: ${error.message}`);
    }
  };

  const handleDeleteNumber = async (customerId, numberToDelete) => {
    console.log(`Delete virtual number ${numberToDelete} for customer ID: ${customerId}`);
    if (!window.confirm(`Are you sure you want to delete virtual number ${numberToDelete}?`)) {
      return;
    }

    try {
      const response = await api.delete(`/account/${customerId}/virtual-numbers/`, {
        data: { number: numberToDelete }, // Pass number in data for DELETE request
      });

      if (!response.data.success) {
        throw new Error(response.data.message || "Failed to delete virtual number");
      }

      setCustomerData((prevData) =>
        prevData.map((customer) =>
          customer.id === customerId
            ? { ...customer, virtualNumbers: customer.virtualNumbers.filter(num => num !== numberToDelete) }
            : customer
        )
      );
    } catch (error) {
      console.error("Error deleting virtual number:", error);
      setError(`Failed to delete virtual number: ${error.message || "Unknown error"}`);
    }
  };

  const handleViewUsers = (customerId) => {
    console.log(`View users for customer ID: ${customerId}`);
    setSelectedCustomerId(customerId);
    setOpenUsersModal(true);
  };

  const handleCloseUsersModal = () => {
    setOpenUsersModal(false);
    setSelectedCustomerId(null);
  };

  const filteredCustomers = customerData.filter((customer) =>
    customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.adminName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.registeredMobileNumber.includes(searchTerm) ||
    customer.emailId.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return <Box sx={{ p: 3 }}><Typography>Loading customers...</Typography></Box>;
  }

  if (error) {
    return <Box sx={{ p: 3 }}><Typography color="error">Error: {error}</Typography></Box>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Cloud Telephony Management
      </Typography>

      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <TextField
          label="Search by company name"
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="cloud telephony customers table">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell align="right">Total Users</TableCell>
              <TableCell align="center">CT Active</TableCell>
              <TableCell align="right">CT Max Users</TableCell>
              <TableCell>Virtual Numbers</TableCell>
              <TableCell>CT Expiry Date</TableCell>
              <TableCell>Vendor</TableCell>
              <TableCell>Users</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredCustomers.map((customer) => (
              <TableRow
                key={customer.id}
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
              >
                <TableCell component="th" scope="row">
                    <Typography variant="subtitle1" component="div">
                      {customer.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Admin: {customer.adminName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Mobile: {customer.registeredMobileNumber}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Email: {customer.emailId}
                    </Typography>
                </TableCell>
                <TableCell align="right">{customer.totalUsers}</TableCell>
                <TableCell align="center">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={customer.ctActive}
                        onChange={() => handleToggleCtActive(customer.id, customer.ctActive)}
                        name="ctActive"
                        color="primary"
                      />
                    }
                    label={customer.ctActive ? 'Active' : 'Inactive'}
                    labelPlacement="top"
                  />
                </TableCell>
                <TableCell align="right">{customer.ctActiveUsers} / {customer.ctMaxUsers}</TableCell> {/* Display CT Active Users / Max Users */}
                <TableCell>
                  {customer.virtualNumbers.map((number, index) => (
                    <Box key={index} display="flex" alignItems="center" mb={0.5}>
                      <Typography variant="body2">{number}</Typography>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteNumber(customer.id, number)}
                        sx={{ ml: 0.5 }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => handleAddNumber(customer.id)}
                    sx={{ ml: 1 }}
                  >
                    <AddCircleOutlineIcon fontSize="small" />
                  </IconButton>
                </TableCell>
                <TableCell>{customer.ctExpiryDate}</TableCell>
                <TableCell>{customer.vendor}</TableCell>
                <TableCell>
                  <Button 
                    variant="contained" 
                    size="small" 
                    onClick={() => handleViewUsers(customer.id)}
                    sx={{ bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } }}
                  >
                    Users
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Modal
        open={openUsersModal}
        onClose={handleCloseUsersModal}
        aria-labelledby="customer-users-modal-title"
        aria-describedby="customer-users-modal-description"
      >
        <Box sx={style}>
          <CustomerUsers customerId={selectedCustomerId} handleClose={handleCloseUsersModal} />
        </Box>
      </Modal>

      {/* New Modal for CT Settings */}
      <Modal
        open={openCtSettingsModal}
        onClose={() => setOpenCtSettingsModal(false)}
        aria-labelledby="ct-settings-modal-title"
        aria-describedby="ct-settings-modal-description"
      >
        <Box sx={style}>
          <Typography id="ct-settings-modal-title" variant="h6" component="h2" gutterBottom>
            Cloud Telephony Settings
          </Typography>
          <TextField
            label="Max Users"
            type="number"
            fullWidth
            margin="normal"
            value={tempCtSettings.max_users}
            onChange={(e) => setTempCtSettings({ ...tempCtSettings, max_users: parseInt(e.target.value) || 0 })}
          />
          <TextField
            label="Expiry Date"
            type="date"
            fullWidth
            margin="normal"
            InputLabelProps={{ shrink: true }}
            value={tempCtSettings.expiry_date}
            onChange={(e) => setTempCtSettings({ ...tempCtSettings, expiry_date: e.target.value })}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button onClick={() => setOpenCtSettingsModal(false)} sx={{ mr: 1 }}>
              Cancel
            </Button>
            <Button variant="contained" onClick={() => handleSaveCtSettings()} >
              Save
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* New Modal for Add Virtual Number */}
      <Modal
        open={openAddNumberModal}
        onClose={() => setOpenAddNumberModal(false)}
        aria-labelledby="add-virtual-number-modal-title"
        aria-describedby="add-virtual-number-modal-description"
      >
        <Box sx={style}>
          <Typography id="add-virtual-number-modal-title" variant="h6" component="h2" gutterBottom>
            Add New Virtual Number
          </Typography>
          <TextField
            label="Virtual Number"
            fullWidth
            margin="normal"
            value={newVirtualNumber}
            onChange={(e) => setNewVirtualNumber(e.target.value)}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button onClick={() => setOpenAddNumberModal(false)} sx={{ mr: 1 }}>
              Cancel
            </Button>
            <Button variant="contained" onClick={() => handleAddNumberConfirm()} >
              Add Number
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 800, // Adjust width as needed
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
  p: 4,
  maxHeight: '90vh',
  overflowY: 'auto',
};

export default CloudTelephony;
